import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import JobPostingForm from '../components/Pharmacy/JobPostingForm'
import { useAuth } from '../providers/AuthProvider'
import { supabase } from '../lib/supabaseClient'

export default function Pharmacy() {
  const { user, role } = useAuth()
  const isPharmacy = role === 'pharmacy'

  // 공고 목록 상태
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [activeTab, setActiveTab] = useState('all') // 'all', 'my'
  const [editingJob, setEditingJob] = useState(null) // ✅ 수정 중인 공고

  // 공고 목록 불러오기
  const loadJobs = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('job_posts')
        .select('*') // Select all fields to support editing pre-fill
        .order('created_at', { ascending: false })

      // 내 공고만 필터링
      if (activeTab === 'my' && user) {
        query = query.eq('created_by', user.id)
      } else {
        query = query.eq('status', 'open')
      }

      const { data, error } = await query
      if (error) {
        console.error('loadJobs error:', error)
        setJobs([])
      } else {
        setJobs(data || [])
      }
    } catch (e) {
      console.error('loadJobs exception:', e)
      setJobs([])
    } finally {
      setLoading(false)
    }
  }, [activeTab, user])

  useEffect(() => {
    loadJobs()
  }, [loadJobs])

  // 공고 등록/수정 완료 핸들러
  const handlePosted = () => {
    setShowForm(false)
    setEditingJob(null)
    loadJobs()
    // alert is handled in form
  }

  // 남은 일수 계산
  const getDaysLeft = (dateStr) => {
    if (!dateStr) return null
    const target = new Date(dateStr)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24))
    return diff
  }

  const handleEdit = (e, job) => {
    e.stopPropagation() // Prevent card click
    setEditingJob(job)
    setShowForm(true)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">구인구직</h1>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* 탭 */}
        <div className="max-w-2xl mx-auto px-4 flex gap-1 border-t bg-gray-50">
          <button
            onClick={() => { setActiveTab('all'); setEditingJob(null); }}
            className={`flex-1 py-2.5 text-sm font-medium transition ${activeTab === 'all'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            전체 공고
          </button>
          {isPharmacy && (
            <button
              onClick={() => { setActiveTab('my'); setEditingJob(null); }}
              className={`flex-1 py-2.5 text-sm font-medium transition ${activeTab === 'my'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              내 공고
            </button>
          )}
        </div>
      </header>

      {/* 공고 목록 */}
      <main className="max-w-2xl mx-auto px-4 py-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-3">📋</div>
            <p className="text-gray-500 text-sm">
              {activeTab === 'my' ? '등록한 공고가 없습니다.' : '현재 모집 중인 공고가 없습니다.'}
            </p>
            {isPharmacy && activeTab === 'my' && (
              <button
                onClick={() => { setEditingJob(null); setShowForm(true); }}
                className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition"
              >
                첫 공고 등록하기
              </button>
            )}
          </div>
        ) : (
          <ul className="space-y-3">
            {jobs.map((job) => {
              const daysLeft = getDaysLeft(job.date)
              return (
                <li
                  key={job.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer relative group"
                  onClick={() => {/* TODO: 상세 페이지 이동 */ }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* 약국명/지역 */}
                      <p className="text-xs text-blue-600 font-medium mb-1 truncate">
                        {job.region} {job.district}
                      </p>
                      {/* 제목 */}
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                        {job.title}
                      </h3>
                      {/* 남은 기간 */}
                      <p className="text-xs text-gray-500">
                        {daysLeft !== null && daysLeft >= 0 ? (
                          <>
                            {daysLeft === 0 ? '오늘 마감' : `${daysLeft}일 남음`}
                            <span className="text-gray-300 mx-1">·</span>
                          </>
                        ) : null}
                        {job.date}
                      </p>
                    </div>
                    {/* 급여 정보 */}
                    <div className="flex flex-col items-end gap-2">
                      {job.salary && (
                        <div className="flex-shrink-0 text-right">
                          <span className="text-xs text-gray-500">{job.duration_type || '일당'}</span>
                          <p className="text-sm font-bold text-gray-900">{job.salary}</p>
                        </div>
                      )}
                      {/* ✅ 수정 버튼 (내 공고 탭 + 약국 권한) */}
                      {isPharmacy && activeTab === 'my' && (
                        <button
                          onClick={(e) => handleEdit(e, job)}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold rounded-lg transition z-10"
                        >
                          수정
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </main>

      {/* Floating Action Button (FAB) : 공고 등록 (약국용) */}
      {isPharmacy && (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-40 flex items-center gap-5 drop-shadow-xl animate-fade-in-up">
          {/* 문의 버튼 */}
          <button
            onClick={() => window.open('https://pf.kakao.com/_xxxx', '_blank')}
            className="bg-slate-700 hover:bg-slate-800 text-white rounded-full px-5 py-3 font-bold text-sm transition active:scale-95 whitespace-nowrap"
          >
            문의
          </button>

          {/* 공고 등록 버튼 (Main) */}
          <button
            onClick={() => { setEditingJob(null); setShowForm(true); }}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6 py-3 font-bold text-sm flex items-center gap-2 transition active:scale-95 whitespace-nowrap"
          >
            공고 등록
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          </button>

          {/* 새로고침 버튼 */}
          <button
            onClick={loadJobs}
            className="bg-white hover:bg-gray-50 text-gray-500 rounded-full w-11 h-11 flex items-center justify-center transition active:scale-95 active:rotate-180"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
        </div>
      )}

      {/* 비로그인 유저를 위한 안내 FAB (로그인 유도) */}
      {!user && (
        <Link
          to="/login"
          className="fixed bottom-20 right-4 w-14 h-14 bg-gray-800 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-700 transition z-40"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
        </Link>
      )}

      {/* 공고 등록 모달 */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-xl">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">{editingJob ? '공고 수정' : '공고 등록'}</h2>
              <button
                onClick={() => { setShowForm(false); setEditingJob(null); }}
                className="p-2 text-gray-400 hover:text-gray-600 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {!user && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  ⚠️ 공고 등록은 <span className="font-semibold">로그인 후 약국 계정</span>으로만 가능합니다.
                </div>
              )}
              {user && !isPharmacy && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                  ⚠️ 현재 계정은 약국 계정이 아닙니다. 공고 등록은 <span className="font-semibold">약국 계정</span>으로만 가능합니다.
                </div>
              )}
              <JobPostingForm
                onPosted={handlePosted}
                editing={editingJob}
                onCancelEdit={() => { setEditingJob(null); setShowForm(false); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
